import Typesense from 'typesense'
import type { SearchResults } from '#shared/types/search'
import { getPublicProjectListingFilter } from '#shared/utils/publicProjectFilters'

const COLLECTION_VALID_FACETS: Record<string, string[]> = {
    jobs: [
        'topics',
        'type',
        'who',
        'jobLocation',
        'jobPlace',
        'geoCity',
        'geoCountry',
        'geoRegion',
        'remote',
        'visibility',
        'orgType'
    ],
    skills: ['topics', 'type', 'visibility'],
    mentors: [
        'interests',
        'mentorInterests',
        'mentorWho',
        'mentorExpertise',
        'mentorTopInterests',
        'visibility',
        'geoCity',
        'geoCountry',
        'geoRegion',
        'mentorTopApproved'
    ],
    mentees: [
        'interests',
        'geoCity',
        'geoCountry',
        'geoRegion',
        'visibility',
        'menteeBooked',
        'menteeTopApproved',
        'menteePreferredSegments'
    ],
    projects: [
        'topics',
        'geoCity',
        'geoCountry',
        'geoRegion',
        'remote',
        'visibility',
        'orgType',
        'orgTitle',
        'who',
        'whoGroupEnabled',
        'duration',
        'how',
        'requiresProfessionalExperience',
        'disabilities'
    ],
    orgs: ['type', 'geocity', 'country', 'interests'],
    volunteers: [
        'interests',
        'geoCity',
        'geoCountry',
        'geoRegion',
        'visibility',
        'volunteerInterests',
        'volunteerHelpWho',
        'volunteerTimeCapacity',
        'volunteerCooperation',
        'volunteerGeoRegion',
        'volunteerPrefersFieldOfExperience'
    ],
    blogs: ['status', 'isFeatured', 'tags', 'categories', 'authorName', 'authorId'],
    community_members: [
        'communityId',
        'visibility',
        'geoCity',
        'geoCountry',
        'geoRegion',
        'interests',
        'locationCountry',
        'favoriteLocationCountry'
    ],
    talentbase_members: ['status', 'geoCity', 'geoCountry', 'geoRegion', 'interests']
}

/**
 * Composable for Typesense search functionality
 */
export function useTypesense() {
    const config = useRuntimeConfig()

    // Initialize client
    const client = new Typesense.Client({
        nodes: [
            {
                host: config.public.TYPESENSE_HOST as string,
                port: Number(config.public.TYPESENSE_PORT),
                protocol: config.public.TYPESENSE_PROTOCOL as 'http' | 'https'
            }
        ],
        apiKey: config.public.TYPESENSE_API_KEY as string,
        connectionTimeoutSeconds: 10
    })

    const DEFAULT_PER_PAGE = 25

    /**
     * Semantic search with vector embeddings
     * This is the main search method for natural language queries
     */
    async function semanticSearch<T = any>(
        collection: string,
        query: string,
        options: {
            page?: number
            hitsPerPage?: number
            filters?: string
            filterBy?: string
            facetBy?: string
        } = {}
    ): Promise<SearchResults<T>> {
        try {
            const searchParams = {
                q: query || '*',
                query_by: 'embedding', // Pure semantic search via auto-embeddings
                filter_by: options.filterBy || options.filters || '',
                facet_by: options.facetBy || '',
                per_page: options.hitsPerPage || DEFAULT_PER_PAGE,
                page: (options.page || 0) + 1, // Typesense is 1-indexed
                exclude_fields: 'embedding', // Don't return embedding vectors (save bandwidth)
                prefix: false // Remote embedders don't support prefix search
            }

            const response = await client.collections(collection).documents().search(searchParams)

            return transformTypesenseResponse(response, collection)
        } catch (error: any) {
            console.error('[Typesense] Semantic search error:', error)
            // Re-throw the error so the UI can display it to the user
            throw new Error(`Typesense search failed: ${error.message || error}`)
        }
    }

    /**
     * Hybrid search (keyword + semantic)
     * Use when you want to combine exact keyword matching with semantic understanding
     * This is the RECOMMENDED search method for authenticated users
     */
    async function hybridSearch<T = any>(
        collection: string,
        query: string,
        options: {
            page?: number
            hitsPerPage?: number
            per_page?: number
            filters?: string
            filterBy?: string
            facetBy?: string
            queryBy?: string // Override text fields to search
            queryByWeights?: string // Weights for each field in queryBy
            alpha?: number // 0 = pure semantic, 1 = pure keyword, 0.5 = balanced (default: 0.3)
            sort_by?: string
        } = {}
    ): Promise<SearchResults<T>> {
        try {
            // Collection-specific hybrid query fields (embedding + text fields)
            // Format: embedding first, then text fields for hybrid ranking
            const hybridQueryByFields: Record<string, string> = {
                jobs: 'embedding,title,summary,description,topics,orgName',
                mentors: 'embedding,shortProfile,mentorInterests,mentorExpertise,mentorTopInterests,jobsText,interests,firstName,lastName',
                mentees: 'embedding,goals,currentSituation,shortProfile,menteePreferredSegments,interests,firstName,lastName',
                projects: 'embedding,title,description,topics',
                skills: 'embedding,title,description,topics',
                volunteers: 'embedding,interests,firstName,lastName',
                orgs: 'embedding,title,desc',
                blogs: 'embedding,title,excerpt,content'
            }

            // Collection-specific weights (embedding weight, then text field weights)
            // Higher embedding weight = more semantic, lower = more keyword
            const hybridWeights: Record<string, string> = {
                jobs: '7,3,2,1,1,1', // Semantic-heavy for job matching
                mentors: '6,3,2,2,2,2,1,1,1', // Balanced for mentor matching
                mentees: '6,3,2,2,2,1,1,1', // Balanced for mentee matching
                projects: '7,3,2,1', // Semantic-heavy
                skills: '7,3,2,1', // Semantic-heavy
                volunteers: '6,2,1,1', // Balanced
                orgs: '7,3,2', // Semantic-heavy
                blogs: '5,3,2,1' // More keyword for exact blog searches
            }

            const queryBy = options.queryBy || hybridQueryByFields[collection] || 'embedding,title'

            // Calculate weights based on alpha (if provided) or use defaults
            let queryByWeights = options.queryByWeights
            if (!queryByWeights) {
                if (options.alpha !== undefined) {
                    // alpha controls semantic vs keyword balance
                    // alpha = 0 => pure semantic (embedding weight high)
                    // alpha = 1 => pure keyword (text field weights high)
                    const semanticWeight = Math.round((1 - options.alpha) * 10)
                    const keywordWeight = Math.round(options.alpha * 3)

                    // Generate weights for each field in queryBy
                    const fields = queryBy.split(',')
                    const weights = fields.map((field) =>
                        field.includes('embedding') ? semanticWeight : keywordWeight
                    )
                    queryByWeights = weights.join(',')
                } else {
                    // Use collection-specific default weights
                    queryByWeights = hybridWeights[collection]
                }
            }

            // Typesense applies a default cosine distance_threshold (~0.30) to vector matches.
            // For long natural-language personalization queries against niche content, NO doc
            // sits inside that band → hybrid returns 0 hits. Relax the threshold so embedding
            // ranking always surfaces the top semantically-closest docs.
            const hasEmbedding = queryBy.includes('embedding')
            const perPage = options.per_page || options.hitsPerPage || DEFAULT_PER_PAGE

            const searchParams: Record<string, unknown> = {
                q: query || '*',
                query_by: queryBy,
                query_by_weights: queryByWeights,
                filter_by: options.filterBy || options.filters || '',
                facet_by: options.facetBy || '',
                per_page: perPage,
                page: (options.page || 0) + 1,
                exclude_fields: 'embedding',
                prefix: false, // Disable prefix matching for hybrid search (remote embedders don't support it)
                sort_by: options.sort_by
            }

            if (hasEmbedding && query && query !== '*') {
                searchParams.vector_query = `embedding:([], k:${perPage * 4}, distance_threshold:2.0)`
            }

            // Sent as a POST multi_search: a GET puts everything in the query string, which Typesense
            // caps at 4000 characters, and long personalization queries used to hit that limit.
            const response = await client.multiSearch.perform({ searches: [{ collection, ...searchParams }] }, {})
            const result: any = response.results[0]
            // multi_search answers 200 and reports a failed search inside the result instead
            if (!result || result.error) {
                throw new Error(result?.error || 'Empty multi_search response')
            }

            return transformTypesenseResponse(result, collection)
        } catch (error: any) {
            console.error('[Typesense] Hybrid search error:', error)
            // Re-throw the error so the UI can display it to the user
            throw new Error(`Typesense hybrid search failed: ${error.message || error}`)
        }
    }

    /**
     * Keyword search (traditional text search)
     * Use for filtering and searching by specific text fields
     */
    async function keywordSearch<T = any>(
        collection: string,
        query: string,
        options: {
            page?: number
            per_page?: number
            hitsPerPage?: number
            filters?: string
            filterBy?: string
            facetBy?: string
            queryBy?: string
            sort_by?: string
            prefix?: boolean
            includeFields?: string // Return only these document fields (e.g. 'id' when only nbHits matters)
        } = {}
    ): Promise<SearchResults<T>> {
        try {
            // Collection-specific query fields
            const queryByFields: Record<string, string> = {
                jobs: 'title,description,summary',
                mentors:
                    'firstName,lastName,shortProfile,interests,jobsText,universitiesText,mentorInterests,mentorWho,mentorExpertise,mentorTopInterests',
                mentees:
                    'firstName,lastName,shortProfile,interests,goals,currentSituation,menteePreferredSegments',
                projects: 'title,description',
                skills: 'title,description',
                volunteers: 'firstName,lastName,interests',
                orgs: 'title,desc',
                blogs: 'title,content,excerpt',
                community_members: 'name,firstName,lastName,shortProfile,interests'
            }

            const queryBy = options.queryBy || queryByFields[collection] || 'title'
            // Disable prefix search if embedding field is included (hybrid search)
            const usePrefix = options.prefix !== undefined ? options.prefix : !queryBy.includes('embedding')

            const searchParams = {
                q: query || '*',
                query_by: queryBy,
                filter_by: options.filterBy || options.filters || '',
                facet_by: options.facetBy || '',
                per_page: options.per_page || options.hitsPerPage || DEFAULT_PER_PAGE,
                page: (options.page || 0) + 1,
                exclude_fields: 'embedding',
                include_fields: options.includeFields,
                sort_by: options.sort_by || (query ? undefined : 'createdAt:desc'),
                prefix: usePrefix
            }

            const response = await client.collections(collection).documents().search(searchParams)

            return transformTypesenseResponse(response, collection)
        } catch (error: any) {
            console.error('[Typesense] Keyword search error:', error)
            // Re-throw the error so the UI can display it to the user
            throw new Error(`Typesense keyword search failed: ${error.message || error}`)
        }
    }

    /**
     * Load facets from collection
     */
    async function loadFacets<T extends Record<string, any>>(
        collection: string,
        options: {
            facets?: string[]
            filters?: string
            filterBy?: string
            queryBy?: string
        } = {}
    ): Promise<T> {
        try {
            // Map collection-specific query fields
            const queryByFields: Record<string, string> = {
                jobs: 'title,description,summary',
                mentors:
                    'firstName,lastName,shortProfile,interests,jobsText,universitiesText,mentorInterests,mentorWho,mentorExpertise,mentorTopInterests',
                mentees:
                    'firstName,lastName,shortProfile,interests,goals,currentSituation,menteePreferredSegments',
                projects: 'title,description',
                skills: 'title,description',
                volunteers: 'firstName,lastName,interests',
                orgs: 'title,desc',
                blogs: 'title,content,excerpt',
                community_members: 'name,firstName,lastName,shortProfile,interests'
            }

            // Collection-specific facet mappings
            // Maps UI filter names to Typesense schema field names
            const collectionFacetMappings: Record<string, Record<string, string>> = {
                jobs: {
                    // Direct mappings - schema now has these fields
                    'type': 'type',
                    'who': 'who',
                    'jobLocation': 'jobLocation',
                    'jobPlace': 'jobPlace',
                    'orgMeta.type': 'orgType'
                },
                skills: {
                    // No remapping needed for skills facets.
                },
                mentors: {
                    'orgMeta.type': 'orgType',
                    'orgMeta.key': 'orgKey'
                }
            }

            // Get the mapping for this collection (fallback to empty if not defined)
            const facetMapping = collectionFacetMappings[collection] || {}

            // Transform facet names using collection-specific mapping
            const mappedFacets = options.facets?.map((f) => facetMapping[f] || f).filter(Boolean) || []

            // Filter to only valid facets for this collection.
            const validFacetsForCollection = COLLECTION_VALID_FACETS[collection] || []
            const requestedFacets = mappedFacets.length ? mappedFacets : validFacetsForCollection
            const validFacets = [...new Set(requestedFacets)].filter((f) => validFacetsForCollection.includes(f))

            // If the request only contains invalid facets for this collection, return empty facet buckets.
            if (!validFacets.length) {
                const emptyResult: Record<string, Record<string, number>> = {}
                options.facets?.forEach((facet) => {
                    emptyResult[facet] = {}
                })
                return emptyResult as T
            }

            const searchParams = {
                q: '*',
                query_by: options.queryBy || queryByFields[collection] || 'title',
                facet_by: validFacets.join(','),
                filter_by: options.filterBy || options.filters || '',
                per_page: 0,
                max_facet_values: 100
            }

            const response = await client.collections(collection).documents().search(searchParams)

            const facetResult: Record<string, Record<string, number>> = (response.facet_counts || []).reduce(
                (acc, facet) => {
                    acc[facet.field_name] = facet.counts.reduce(
                        (valAcc, count) => {
                            valAcc[count.value] = count.count
                            return valAcc
                        },
                        {} as Record<string, number>
                    )
                    return acc
                },
                {} as Record<string, Record<string, number>>
            )

            // Add empty objects for requested but unavailable facets (backward compat)
            options.facets?.forEach((facet) => {
                if (!facetResult[facet]) {
                    facetResult[facet] = {}
                }
            })

            return facetResult as T
        } catch (error) {
            console.error('[Typesense] Error loading facets:', error)
            // Return empty objects for all requested facets
            const emptyResult: Record<string, Record<string, number>> = {}
            options.facets?.forEach((facet) => {
                emptyResult[facet] = {}
            })
            return emptyResult as T
        }
    }

    /**
     * Transform Typesense response to match Search interface
     * This ensures backward compatibility with existing components
     */
    function transformTypesenseResponse<T>(response: any, collection: string): SearchResults<T> {
        const hits = (response.hits || []).map((hit: any) => {
            const doc = {
                ...hit.document,
                objectID: hit.document.id,
                _highlightResult: hit.highlights || {},
                // Preserve Typesense metadata for match scoring
                _typesense: {
                    vector_distance: hit.vector_distance,
                    hybrid_search_info: hit.hybrid_search_info,
                    text_match_score: hit.text_match_score
                }
            }

            // Add missing structure for jobs (backward compatibility with Search)
            if (collection === 'jobs') {
                // Preserve existing orgMeta from Typesense, use flat fields as fallback
                doc.orgMeta = hit.document.orgMeta || {
                    image: doc.orgImage || '',
                    title: doc.orgName || '',
                    type: doc.orgType || '',
                    key: doc.orgId || ''
                }
                doc.endDate = doc.endDate || doc.createdAt || Date.now()
                doc.jobPlace = doc.jobPlace || (doc.remote ? 'hybrid' : 'onsite')
            }

            return doc
        })

        return {
            hits,
            nbHits: response.found || 0,
            page: (response.page || 1) - 1, // Convert to 0-indexed
            nbPages: Math.ceil((response.found || 0) / (response.request_params?.per_page || DEFAULT_PER_PAGE)),
            hitsPerPage: response.request_params?.per_page || DEFAULT_PER_PAGE,
            processingTimeMS: response.search_time_ms || 0,
            exhaustiveNbHits: true,
            exhaustiveTypo: true,
            query: response.request_params?.q || '',
            params: JSON.stringify(response.request_params || {}),
            index: collection
        }
    }

    function createEmptyResults<T>(collection: string): SearchResults<T> {
        return {
            hits: [],
            nbHits: 0,
            page: 0,
            nbPages: 0,
            hitsPerPage: DEFAULT_PER_PAGE,
            processingTimeMS: 0,
            exhaustiveNbHits: true,
            exhaustiveTypo: true,
            query: '',
            params: '',
            index: collection
        }
    }

    /**
     * Serialize filters for Typesense
     * Convert search-style filters to Typesense format
     */
    function serializeFilters(filters: Record<string, any>, options: { disjunction?: boolean } = {}): string {
        return Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => {
                if (Array.isArray(value)) {
                    if (value.length === 0) return ''
                    // Typesense uses [val1, val2] syntax for OR within a field
                    return `${key}:[${value.map((v) => `\`${v}\``).join(',')}]`
                }
                // Boolean values should not be wrapped in backticks
                if (typeof value === 'boolean') {
                    return `${key}:=${value}`
                }
                return `${key}:=\`${value}\``
            })
            .filter(Boolean)
            .join(options.disjunction ? ' || ' : ' && ')
    }

    async function getRelatedItems<T = any>(
        collection: string,
        currentItemId: string,
        options: {
            maxItems?: number
            query?: string // Optional query text for semantic matching
            filterBy?: string
        } = {}
    ): Promise<SearchResults<T>> {
        try {
            const maxItems = options.maxItems || 8

            // Recommendations must obey the same public availability rules as listings.
            const publicFilter = collection === 'projects'
                ? getPublicProjectListingFilter()
                : collection === 'jobs'
                  ? `visibility:=published && endDate:>${Date.now()}`
                  : 'visibility:=published'
            const safeId = /^[\w-]{1,200}$/.test(currentItemId) ? currentItemId : ''
            let filterBy = publicFilter
            if (safeId) {
                filterBy += ` && id:!=\`${safeId}\``
                if (collection === 'jobs' || collection === 'skills') filterBy += ` && slug:!=\`${safeId}\``
            }
            if (options.filterBy) {
                filterBy = `${filterBy} && (${options.filterBy})`
            }

            // Prefer vector similarity (using the current doc's embedding) for "related" items.
            // Falls back to plain keyword search if the collection has no embedding field.
            try {
                if (!safeId) throw new Error('No source item available')
                const response = await client
                    .collections(collection)
                    .documents()
                    .search({
                        q: '*',
                        vector_query: `embedding:([], id:${safeId})`,
                        filter_by: filterBy,
                        per_page: maxItems,
                        exclude_fields: 'embedding'
                    } as any)

                if (response?.hits?.length) {
                    return transformTypesenseResponse<T>(response, collection)
                }
            } catch (vectorErr: any) {
                console.warn('[Typesense] Vector related-items search failed, falling back to keyword:', vectorErr?.message || vectorErr)
            }

            // Removed items may no longer have an embedding. Their public URL slug still
            // provides useful search terms; never fetch a private source document here.
            const query = options.query || (safeId.includes('-') ? safeId.replace(/-/g, ' ') : '*')
            const result = await keywordSearch<T>(collection, query, {
                hitsPerPage: maxItems,
                filterBy
            })
            if (result.hits.length || query === '*') return result
            return await keywordSearch<T>(collection, '*', { hitsPerPage: maxItems, filterBy, sort_by: 'createdAt:desc' })
        } catch (error: any) {
            console.error('[Typesense] Error getting related items:', error)
            return {
                hits: [],
                nbHits: 0,
                page: 0,
                nbPages: 0,
                hitsPerPage: options.maxItems || 8,
                processingTimeMS: 0,
                exhaustiveNbHits: true,
                exhaustiveTypo: true,
                query: '',
                params: '',
                index: collection
            }
        }
    }

    /**
     * Federated search across multiple collections
     * Replaces the need for a merged_opportunities collection
     */
    async function federatedSearch<T = any>(
        collections: Array<{
            collection: string
            queryBy?: string
            filterBy?: string
            weight?: number
        }>,
        query: string,
        options: {
            page?: number
            hitsPerPage?: number
        } = {}
    ): Promise<{
        results: Array<SearchResults<T>>
        combined: SearchResults<T>
    }> {
        try {
            const queryByFields: Record<string, string> = {
                jobs: 'title,description,summary,orgName',
                mentors: 'firstName,lastName,shortProfile,interests,jobsText',
                projects: 'title,description,orgTitle',
                skills: 'title,description,orgTitle',
                volunteers: 'firstName,lastName,interests',
                orgs: 'title,desc',
                blogs: 'title,excerpt,content',
                searchable_pages_data: 'title,description,content'
            }

            const searches = collections.map((col) => ({
                collection: col.collection,
                q: query || '*',
                query_by: col.queryBy || queryByFields[col.collection] || 'title',
                filter_by: col.filterBy || '',
                per_page: options.hitsPerPage || DEFAULT_PER_PAGE,
                page: (options.page || 0) + 1,
                exclude_fields: 'embedding',
                prefix: true
            }))

            const response = await client.multiSearch.perform({ searches })

            // Transform each collection's results, skipping failed collections
            const results = response.results.map((result: any, index: number) => {
                const collectionName = collections[index]?.collection || 'unknown'
                // Handle individual collection errors (e.g., collection not found)
                if (result.error) {
                    console.warn(`[Typesense] Collection "${collectionName}" error:`, result.error)
                    return createEmptyResults<T>(collectionName)
                }
                return transformTypesenseResponse<T>(result, collectionName)
            })

            // Combine all hits with type annotation
            const allHits = results.flatMap((r, index) => {
                const collectionName = collections[index]?.collection || 'unknown'
                const typeMapping: Record<string, string> = {
                    jobs: 'job',
                    projects: 'project',
                    skills: 'skill',
                    mentors: 'mentor',
                    volunteers: 'volunteer',
                    orgs: 'org',
                    blogs: 'blog',
                    searchable_pages_data: 'page'
                }
                return r.hits.map((hit: any) => ({
                    ...hit,
                    _collection: collectionName,
                    _index: collectionName, // For backward compatibility with search-style templates
                    type: hit.type || typeMapping[collectionName] || collectionName
                }))
            })

            // Sort combined results by relevance score or date
            allHits.sort((a: any, b: any) => {
                // Prefer items with higher text match scores
                const scoreA = a._typesense?.text_match_score || 0
                const scoreB = b._typesense?.text_match_score || 0
                if (scoreA !== scoreB) return scoreB - scoreA
                // Fall back to creation date
                return (b.createdAt || 0) - (a.createdAt || 0)
            })

            // Paginate combined results
            const perPage = options.hitsPerPage || DEFAULT_PER_PAGE
            const startIndex = (options.page || 0) * perPage
            const paginatedHits = allHits.slice(startIndex, startIndex + perPage)

            const combined: SearchResults<T> = {
                hits: paginatedHits,
                nbHits: allHits.length,
                page: options.page || 0,
                nbPages: Math.ceil(allHits.length / perPage),
                hitsPerPage: perPage,
                processingTimeMS: response.results.reduce((sum: number, r: any) => sum + (r.search_time_ms || 0), 0),
                exhaustiveNbHits: true,
                exhaustiveTypo: true,
                query: query,
                params: '',
                index: 'merged_opportunities'
            }

            return { results, combined }
        } catch (error: any) {
            console.error('[Typesense] Federated search error:', error)
            throw new Error(`Typesense federated search failed: ${error.message || error}`)
        }
    }

    /**
     * Join search results for pagination
     * Combines new results with existing paged results
     */
    function joinSearchResults<T>(
        newResults: SearchResults<T>,
        existingResults?: SearchResults<T> & { pagedHits?: Record<number, T[]> }
    ): SearchResults<T> & { pagedHits: Record<number, T[]> } {
        if (!existingResults) {
            return {
                ...newResults,
                pagedHits: { [newResults.page]: newResults.hits }
            }
        }

        const existingPagedHits = existingResults.pagedHits || {}

        return {
            ...newResults,
            pagedHits: {
                ...existingPagedHits,
                [newResults.page]: newResults.hits
            }
        }
    }

    /**
     * Load facets from multiple collections (federated)
     */
    async function loadFederatedFacets<T extends Record<string, any>>(
        collections: Array<{
            collection: string
            filterBy?: string
        }>,
        options: {
            facets?: string[]
        } = {}
    ): Promise<T> {
        try {
            const queryByFields: Record<string, string> = {
                jobs: 'title',
                mentors: 'firstName',
                projects: 'title',
                skills: 'title',
                volunteers: 'firstName',
                orgs: 'title'
            }

            const searches = collections
                .map((col) => {
                    const validFacetsForCollection = COLLECTION_VALID_FACETS[col.collection] || []
                    const requestedFacets = options.facets?.length ? options.facets : validFacetsForCollection
                    const filteredFacets = requestedFacets.filter((facet) => validFacetsForCollection.includes(facet))

                    // Skip collections where none of the requested facets are valid.
                    if (!filteredFacets.length) {
                        return null
                    }

                    return {
                        collection: col.collection,
                        q: '*',
                        query_by: queryByFields[col.collection] || 'title',
                        filter_by: col.filterBy || '',
                        facet_by: filteredFacets.join(','),
                        per_page: 0,
                        max_facet_values: 100
                    }
                })
                .filter((search): search is NonNullable<typeof search> => search !== null)

            if (!searches.length) {
                return {} as T
            }

            const response = await client.multiSearch.perform({ searches })

            // Merge facets from all collections
            const mergedFacets: Record<string, Record<string, number>> = {}

            response.results.forEach((result: any, index: number) => {
                if (result.error) {
                    const collectionName = searches[index]?.collection || 'unknown'
                    console.warn(
                        `[Typesense] loadFederatedFacets skipped "${collectionName}" due to error:`,
                        result.error
                    )
                    return
                }

                ;(result.facet_counts || []).forEach((facet: any) => {
                    const facetFieldName = facet.field_name as string
                    const facetBucket = mergedFacets[facetFieldName] || (mergedFacets[facetFieldName] = {})
                    facet.counts.forEach((count: any) => {
                        const existing = facetBucket[count.value] || 0
                        facetBucket[count.value] = existing + count.count
                    })
                })
            })

            return mergedFacets as T
        } catch (error) {
            console.error('[Typesense] Error loading federated facets:', error)
            return {} as T
        }
    }

    return {
        client,
        keywordSearch,
        semanticSearch,
        hybridSearch,
        loadFacets,
        serializeFilters,
        joinSearchResults,
        getRelatedItems,
        federatedSearch,
        loadFederatedFacets
    }
}
